import React from "react";
import { Users, Database, Upload } from "lucide-react";
import { buildUsageItem, getUsageMetric } from "./subscriptionUtils";

const ProgressBar = ({ value }) => (
  <div className="progress-track">
    <div className="progress-fill" style={{ width: `${value}%` }} />
  </div>
);

const UsageCard = ({ subscription, isLoading, isError }) => {
  if (isLoading) {
    return <div className="usage-card">Loading usage...</div>;
  }

  if (isError) {
    return <div className="usage-card">Unable to load usage data.</div>;
  }

  const limits = subscription?.plan?.limits ?? {};
  const usage = subscription?.usage ?? subscription?.usageProgress ?? {};
  const users = subscription?.users ?? {};

  const teamSeats = buildUsageItem({
    used: getUsageMetric(subscription, [
      "users.used",
      "usage.teamSeats.used",
      "usage.teamSeatsUsed",
      "usedSeats",
      "teamSeatsUsed",
    ]) ?? users?.used ?? usage?.teamSeats?.used,
    limit:
      getUsageMetric(subscription, [
        "users.limit",
        "usage.teamSeats.limit",
        "usage.teamSeatsLimit",
        "maxSeats",
      ]) ?? users?.limit ?? limits?.maxUsers,
    suffix: " Users",
  });

  const storage = buildUsageItem({
    used: getUsageMetric(subscription, [
      "usage.storage.used",
      "usage.storageUsedGB",
      "storageUsedGB",
      "usedStorageGB",
    ]) ?? usage?.storage?.used,
    limit:
      getUsageMetric(subscription, [
        "usage.storage.limit",
        "usage.storageLimitGB",
        "storageLimitGB",
      ]) ?? limits?.storageSpaceInGB,
    unit: "GB",
    suffix: " GB",
  });

  const dailyUploads = buildUsageItem({
    used: getUsageMetric(subscription, [
      "usage.dailyLeadUploads.used",
      "usage.dailyLeadUploadsUsed",
      "dailyLeadUploadsUsed",
      "usedDailyLeadUploads",
    ]) ?? usage?.dailyLeadUploads?.used,
    limit:
      getUsageMetric(subscription, [
        "usage.dailyLeadUploads.limit",
        "usage.dailyLeadUploadsLimit",
        "dailyLeadUploadsLimit",
        "maxDailyLeadUploads",
      ]) ?? limits?.maxDailyLeadUploads ?? limits?.maxImageUpload,
    suffix: " Daily",
  });

  const teamRemaining =
    teamSeats.limitNum > 0
      ? `${Math.max(teamSeats.limitNum - teamSeats.usedNum, 0)} seats remaining in your current plan.`
      : null;

  return (
    <div className="usage-card">
      <div className="usage-header">
        <h3>Usage Progress</h3>
      </div>

      <div className="usage-item">
        <div className="usage-top">
          <div className="usage-label">
            <Users size={16} />
            <span>Team Seats Used</span>
          </div>
          <span>{teamSeats.label || "—"}</span>
        </div>
        <ProgressBar value={teamSeats.percent} />
        {teamRemaining && <p>{teamRemaining}</p>}
        {!teamRemaining && subscription?.usage?.teamSeats?.message && (
          <p>{subscription.usage.teamSeats.message}</p>
        )}
      </div>

      <div className="usage-item">
        <div className="usage-top">
          <div className="usage-label">
            <Database size={16} />
            <span>Cloud Storage</span>
          </div>
          <span>{storage.label || "—"}</span>
        </div>
        <ProgressBar value={storage.percent} />
        <p>
          {subscription?.usage?.storage?.message ||
            (storage.limitNum > 0
              ? `${Math.max(storage.limitNum - storage.usedNum, 0).toFixed(1)}GB remaining on your current plan.`
              : "")}
        </p>
      </div>

      <div className="usage-item">
        <div className="usage-top">
          <div className="usage-label">
            <Upload size={16} />
            <span>Daily Lead Uploads</span>
          </div>
          <span>{dailyUploads.label || "—"}</span>
        </div>
        <ProgressBar value={dailyUploads.percent} />
        <p>
          {subscription?.usage?.dailyLeadUploads?.resetsIn ||
            subscription?.dailyUploadsResetIn ||
            (dailyUploads.limitNum > 0 ? "" : "—")}
        </p>
      </div>
    </div>
  );
};

export default UsageCard;
